import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';

/**
 * Generate a professional PDF invoice
 * @param {Object} invoice - The invoice data
 * @returns {jsPDF} - The PDF document
 */
export const generateInvoicePDF = (invoice) => {
  const { companyInfo, customerInfo, invoiceInfo, lineItems } = invoice;
  
  // Calculate totals
  const subtotal = lineItems.reduce(
    (sum, item) => sum + item.unitPrice * item.quantity,
    0
  );
  const taxRate = 0.13;
  const tax = subtotal * taxRate;
  const total = subtotal + tax;
  
  // Create PDF document
  const doc = new jsPDF();
  const pageWidth = doc.internal.pageSize.getWidth();
  
  // Colors
  const primaryColor = [30, 64, 124]; // Professional blue
  const textColor = [51, 51, 51];
  const mutedColor = [128, 128, 128];
  
  // Helper function to format currency
  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-CA', {
      style: 'currency',
      currency: 'CAD'
    }).format(amount);
  };
  
  // Header Background
  doc.setFillColor(...primaryColor);
  doc.rect(0, 0, pageWidth, 45, 'F');
  
  // Company Name (White text on blue background)
  doc.setTextColor(255, 255, 255);
  doc.setFontSize(22);
  doc.setFont('helvetica', 'bold');
  doc.text(companyInfo.name || 'Company Name', 15, 18);
  
  // 1001437133 ONTARIO INC.
  doc.setFontSize(10);
  doc.setFont('helvetica', 'normal');
  doc.text('1001437133 ONTARIO INC.', 15, 26);
  
  // INVOICE Title
  doc.setFontSize(28);
  doc.setFont('helvetica', 'bold');
  doc.text('INVOICE', pageWidth - 15, 20, { align: 'right' });
  
  // Invoice Number
  doc.setFontSize(10);
  doc.setFont('helvetica', 'normal');
  doc.text(`#${invoiceInfo.number}`, pageWidth - 15, 28, { align: 'right' });
  
  // Status Badge
  const statusColors = {
    paid: [34, 197, 94],
    pending: [245, 158, 11],
    overdue: [239, 68, 68]
  };
  const statusColor = statusColors[invoiceInfo.status] || statusColors.pending;
  doc.setFillColor(...statusColor);
  doc.roundedRect(pageWidth - 45, 32, 30, 8, 2, 2, 'F');
  doc.setFontSize(8);
  doc.text(invoiceInfo.status.toUpperCase(), pageWidth - 30, 37.5, { align: 'center' });
  
  // Reset text color for body
  doc.setTextColor(...textColor);
  
  // Invoice From Section
  let yPos = 60;
  doc.setFontSize(10);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(...primaryColor);
  doc.text('INVOICE FROM', 15, yPos);
  
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(...textColor);
  yPos += 7;
  doc.setFontSize(11);
  doc.setFont('helvetica', 'bold');
  doc.text(companyInfo.name || '', 15, yPos);
  
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(9);
  doc.setTextColor(...mutedColor);
  yPos += 5;
  doc.text(companyInfo.address || '', 15, yPos);
  yPos += 4;
  doc.text(`${companyInfo.city}, ${companyInfo.province} ${companyInfo.postalCode}`, 15, yPos);
  yPos += 4;
  doc.text(companyInfo.phone || '', 15, yPos);
  yPos += 4;
  doc.text(companyInfo.email || '', 15, yPos);
  
  // HST Number
  if (companyInfo.hstNumber) {
    yPos += 5;
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(...primaryColor);
    doc.text(`HST #: ${companyInfo.hstNumber}`, 15, yPos);
  }
  
  // Invoice To Section
  yPos = 60;
  doc.setFontSize(10);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(...primaryColor);
  doc.text('INVOICE TO', 110, yPos);
  
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(...textColor);
  yPos += 7;
  doc.setFontSize(11);
  doc.setFont('helvetica', 'bold');
  doc.text(customerInfo.name || 'Customer Name', 110, yPos);
  
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(9);
  doc.setTextColor(...mutedColor);
  yPos += 5;
  doc.text(customerInfo.address || '', 110, yPos);
  yPos += 4;
  doc.text(`${customerInfo.city}, ${customerInfo.province} ${customerInfo.postalCode}`, 110, yPos);
  yPos += 4;
  doc.text(customerInfo.phone || '', 110, yPos);
  yPos += 4;
  doc.text(customerInfo.email || '', 110, yPos);
  
  // Invoice Details (Dates)
  yPos = 105;
  doc.setFillColor(245, 247, 250);
  doc.roundedRect(15, yPos - 5, pageWidth - 30, 18, 3, 3, 'F');
  
  doc.setFontSize(8);
  doc.setTextColor(...mutedColor);
  doc.text('INVOICE DATE', 25, yPos);
  doc.text('DUE DATE', 90, yPos);
  
  doc.setFontSize(10);
  doc.setTextColor(...textColor);
  doc.setFont('helvetica', 'bold');
  yPos += 6;
  
  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    return new Date(dateStr).toLocaleDateString('en-CA', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };
  
  doc.text(formatDate(invoiceInfo.date), 25, yPos);
  doc.text(formatDate(invoiceInfo.dueDate), 90, yPos);
  
  // Line Items Table
  yPos = 130;
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(10);
  doc.setTextColor(...primaryColor);
  doc.text('RENTAL ITEMS & SERVICES', 15, yPos);
  
  // Table
  const tableData = lineItems.map(item => [
    item.description,
    formatCurrency(item.unitPrice),
    item.quantity.toString(),
    formatCurrency(item.unitPrice * item.quantity)
  ]);
  
  autoTable(doc, {
    startY: yPos + 5,
    head: [['Description', 'Unit Price', 'Qty/Days', 'Amount']],
    body: tableData,
    theme: 'plain',
    styles: {
      fontSize: 9,
      cellPadding: 5,
      textColor: textColor,
    },
    headStyles: {
      fillColor: [240, 243, 247],
      textColor: textColor,
      fontStyle: 'bold',
      fontSize: 9,
    },
    columnStyles: {
      0: { cellWidth: 80 },
      1: { cellWidth: 35, halign: 'right' },
      2: { cellWidth: 25, halign: 'center' },
      3: { cellWidth: 35, halign: 'right' },
    },
    alternateRowStyles: {
      fillColor: [250, 251, 252],
    },
    margin: { left: 15, right: 15 },
  });
  
  // Totals Section
  const finalY = doc.lastAutoTable?.finalY ? doc.lastAutoTable.finalY + 10 : yPos + 80;
  const totalsX = pageWidth - 80;
  
  doc.setFontSize(9);
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(...mutedColor);
  
  doc.text('Subtotal', totalsX, finalY);
  doc.setTextColor(...textColor);
  doc.text(formatCurrency(subtotal), pageWidth - 15, finalY, { align: 'right' });
  
  doc.setTextColor(...mutedColor);
  doc.text(`HST (${(taxRate * 100).toFixed(0)}%)`, totalsX, finalY + 7);
  doc.setTextColor(...textColor);
  doc.text(formatCurrency(tax), pageWidth - 15, finalY + 7, { align: 'right' });
  
  // Divider line
  doc.setDrawColor(200, 200, 200);
  doc.line(totalsX, finalY + 12, pageWidth - 15, finalY + 12);
  
  // Grand Total
  doc.setFontSize(12);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(...primaryColor);
  doc.text('Grand Total', totalsX, finalY + 20);
  doc.text(formatCurrency(total), pageWidth - 15, finalY + 20, { align: 'right' });
  
  // Notes Section
  const notesY = finalY + 32;
  const notes = invoice.notes || 'Make cheque payable to "1001437133 ONTARIO INC."';
  
  doc.setFillColor(255, 240, 240);
  doc.roundedRect(15, notesY, pageWidth - 30, 25, 2, 2, 'F');
  
  doc.setFontSize(8);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(220, 38, 38); // Red color
  doc.text('NOTES:', 20, notesY + 7);
  
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(10);
  doc.setTextColor(220, 38, 38); // Red color
  doc.text(notes, 20, notesY + 16);
  
  // Footer
  const footerY = 270;
  doc.setDrawColor(220, 220, 220);
  doc.line(15, footerY - 5, pageWidth - 15, footerY - 5);
  
  doc.setFontSize(10);
  doc.setTextColor(...primaryColor);
  doc.setFont('helvetica', 'bold');
  doc.text('Thank you for your business!', pageWidth / 2, footerY, { align: 'center' });
  
  doc.setFontSize(8);
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(...mutedColor);
  doc.text(
    `${companyInfo.name} • ${companyInfo.phone} • ${companyInfo.email}`,
    pageWidth / 2,
    footerY + 7,
    { align: 'center' }
  );
  
  doc.setFontSize(7);
  doc.text(
    'This invoice was generated electronically and is valid without signature.',
    pageWidth / 2,
    footerY + 14,
    { align: 'center' }
  );
  
  return doc;
};

/**
 * Download invoice as PDF
 * @param {Object} invoice - The invoice data
 */
export const downloadInvoicePDF = (invoice) => {
  const doc = generateInvoicePDF(invoice);
  doc.save(`${invoice.invoiceInfo.number}.pdf`);
};

/**
 * Generate a professional PDF for rental agreement
 * @param {Object} agreement - The agreement data
 * @returns {jsPDF} - The PDF document
 */
export const generateAgreementPDF = (agreement) => {
  const { 
    companyInfo, 
    renterInfo, 
    vehicleInfo, 
    rentalPeriod, 
    rentalTerms, 
    agreementInfo,
    insuranceInfo,
    signatureInfo,
    damageChecklist 
  } = agreement;
  
  const doc = new jsPDF();
  const pageWidth = doc.internal.pageSize.getWidth();
  
  // Colors
  const primaryColor = [30, 64, 124];
  const textColor = [51, 51, 51];
  const mutedColor = [128, 128, 128];
  const successColor = [34, 197, 94];
  const warningColor = [245, 158, 11];
  
  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-CA', {
      style: 'currency',
      currency: 'CAD'
    }).format(amount || 0);
  };
  
  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('en-CA', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };
  
  // Header Background
  doc.setFillColor(...primaryColor);
  doc.rect(0, 0, pageWidth, 45, 'F');
  
  // Company Name
  doc.setTextColor(255, 255, 255);
  doc.setFontSize(22);
  doc.setFont('helvetica', 'bold');
  doc.text(companyInfo?.name || 'Ak Car Rental', 15, 20);
  
  doc.setFontSize(10);
  doc.setFont('helvetica', 'normal');
  doc.text('RENTAL AGREEMENT', 15, 28);
  
  // Agreement Number
  doc.setFontSize(14);
  doc.setFont('helvetica', 'bold');
  doc.text(`#${agreementInfo?.number || ''}`, pageWidth - 15, 20, { align: 'right' });
  
  // Status Badge
  const statusColors = {
    active: successColor,
    pending: warningColor,
    completed: [100, 100, 100],
    cancelled: [239, 68, 68]
  };
  const statusColor = statusColors[agreementInfo?.status] || statusColors.pending;
  doc.setFillColor(...statusColor);
  doc.roundedRect(pageWidth - 45, 26, 30, 8, 2, 2, 'F');
  doc.setFontSize(8);
  doc.text((agreementInfo?.status || 'ACTIVE').toUpperCase(), pageWidth - 30, 31.5, { align: 'center' });
  
  // Agreement Date
  doc.setFontSize(9);
  doc.setFont('helvetica', 'normal');
  doc.text(`Date: ${formatDate(agreementInfo?.date)}`, pageWidth - 15, 40, { align: 'right' });
  
  // Reset text color
  doc.setTextColor(...textColor);
  
  // === RENTER INFO ===
  let yPos = 55;
  doc.setFontSize(10);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(...primaryColor);
  doc.text('RENTER INFORMATION', 15, yPos);
  
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(...textColor);
  yPos += 7;
  doc.setFontSize(11);
  doc.setFont('helvetica', 'bold');
  doc.text(renterInfo?.name || '', 15, yPos);
  
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(9);
  doc.setTextColor(...mutedColor);
  yPos += 5;
  doc.text(renterInfo?.address || '', 15, yPos);
  yPos += 4;
  doc.text(`${renterInfo?.city || ''}, ${renterInfo?.province || ''} ${renterInfo?.postalCode || ''}`, 15, yPos);
  yPos += 4;
  doc.text(`Phone: ${renterInfo?.phone || ''}`, 15, yPos);
  yPos += 4;
  doc.text(`Email: ${renterInfo?.email || ''}`, 15, yPos);
  yPos += 4;
  doc.text(`License: ${renterInfo?.driversLicense || ''} (${renterInfo?.licenseProvince || ''})`, 15, yPos);
  
  // === VEHICLE INFO ===
  yPos = 55;
  doc.setFontSize(10);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(...primaryColor);
  doc.text('VEHICLE INFORMATION', 110, yPos);
  
  yPos += 7;
  doc.setFontSize(11);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(...textColor);
  doc.text(`${vehicleInfo?.year || ''} ${vehicleInfo?.make || ''} ${vehicleInfo?.model || ''}`, 110, yPos);
  
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(9);
  doc.setTextColor(...mutedColor);
  yPos += 5;
  doc.text(`Color: ${vehicleInfo?.color || ''}`, 110, yPos);
  yPos += 4;
  doc.text(`License Plate: ${vehicleInfo?.licensePlate || ''}`, 110, yPos);
  yPos += 4;
  doc.text(`VIN: ${vehicleInfo?.vin || ''}`, 110, yPos);
  yPos += 4;
  doc.text(`Odometer Out: ${vehicleInfo?.odometerOut || '-'} km`, 110, yPos);
  yPos += 4;
  doc.text(`Fuel Level Out: ${vehicleInfo?.fuelLevelOut || '-'}`, 110, yPos);
  
  // === RENTAL PERIOD ===
  yPos = 105;
  doc.setFillColor(245, 247, 250);
  doc.roundedRect(15, yPos - 3, pageWidth - 30, 22, 3, 3, 'F');
  
  doc.setFontSize(8);
  doc.setTextColor(...mutedColor);
  doc.text('PICK-UP', 25, yPos + 3);
  doc.text('RETURN', 110, yPos + 3);
  
  doc.setFontSize(10);
  doc.setTextColor(...textColor);
  doc.setFont('helvetica', 'bold');
  doc.text(`${formatDate(rentalPeriod?.pickupDate)} at ${rentalPeriod?.pickupTime || ''}`, 25, yPos + 10);
  doc.text(`${formatDate(rentalPeriod?.returnDate)} at ${rentalPeriod?.returnTime || ''}`, 110, yPos + 10);
  
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(8);
  doc.setTextColor(...mutedColor);
  doc.text(rentalPeriod?.pickupLocation || '', 25, yPos + 16);
  doc.text(rentalPeriod?.returnLocation || '', 110, yPos + 16);
  
  // === RENTAL TERMS ===
  yPos = 135;
  doc.setFontSize(10);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(...primaryColor);
  doc.text('RENTAL TERMS & PRICING', 15, yPos);
  
  yPos += 8;
  doc.setFontSize(9);
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(...mutedColor);
  
  const terms = [
    ['Daily Rate:', formatCurrency(rentalTerms?.dailyRate)],
    ['Deposit:', formatCurrency(rentalTerms?.deposit)],
    ['Mileage Limit:', rentalTerms?.mileageLimit || 'Unlimited'],
    ['Extra Mileage:', `${formatCurrency(rentalTerms?.extraMileageRate)}/km`]
  ];
  
  terms.forEach((term, i) => {
    const x = i < 2 ? 15 : 100;
    const y = yPos + (i % 2) * 6;
    doc.text(term[0], x, y);
    doc.setTextColor(...textColor);
    doc.setFont('helvetica', 'bold');
    doc.text(term[1], x + 35, y);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(...mutedColor);
  });
  
  // === ADDITIONAL OPTIONS ===
  yPos = 160;
  doc.setFontSize(10);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(...primaryColor);
  doc.text('ADDITIONAL OPTIONS', 15, yPos);
  
  yPos += 8;
  doc.setFontSize(9);
  doc.setTextColor(...textColor);
  
  if (insuranceInfo?.winterTiresSelected) {
    doc.text(`✓ Winter Tires: ${formatCurrency(insuranceInfo.winterTiresFee)}`, 15, yPos);
    yPos += 5;
  }
  
  if (insuranceInfo?.hasOwnInsurance) {
    doc.text(`✓ Renter's Own Insurance: ${insuranceInfo.insuranceCompany || ''} - Policy #${insuranceInfo.policyNumber || ''}`, 15, yPos);
    yPos += 5;
  }
  
  // === DAMAGE CHECKLIST ===
  if (damageChecklist?.items?.length > 0) {
    yPos = 185;
    doc.setFontSize(10);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(...primaryColor);
    doc.text('VEHICLE DAMAGE CHECKLIST', 15, yPos);
    
    yPos += 6;
    doc.setFontSize(8);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(...mutedColor);
    doc.text(`Inspected by: ${damageChecklist.inspectedBy || '-'} on ${formatDate(damageChecklist.inspectionDate)}`, 15, yPos);
    
    // List damaged areas only
    const damagedItems = damageChecklist.items.filter(item => item.hasDamage);
    if (damagedItems.length > 0) {
      yPos += 6;
      doc.setTextColor(warningColor[0], warningColor[1], warningColor[2]);
      doc.text(`⚠ ${damagedItems.length} area(s) with noted damage:`, 15, yPos);
      
      yPos += 5;
      doc.setTextColor(...textColor);
      damagedItems.forEach((item, i) => {
        const col = i % 2 === 0 ? 15 : 100;
        const row = Math.floor(i / 2);
        doc.text(`• ${item.area}${item.description ? `: ${item.description}` : ''}`, col, yPos + row * 4);
      });
      yPos += Math.ceil(damagedItems.length / 2) * 4 + 3;
    } else {
      yPos += 6;
      doc.setTextColor(...successColor);
      doc.text('✓ No damage noted - Vehicle in good condition', 15, yPos);
    }
    
    if (damageChecklist.additionalNotes) {
      yPos += 6;
      doc.setTextColor(...mutedColor);
      doc.setFont('helvetica', 'italic');
      doc.text(`Notes: ${damageChecklist.additionalNotes}`, 15, yPos, { maxWidth: pageWidth - 30 });
    }
  }
  
  // === LEGAL WAIVER PAGE ===
  doc.addPage();
  
  // Legal waiver header
  doc.setFillColor(202, 138, 4); // Warning color
  doc.rect(0, 0, pageWidth, 25, 'F');
  
  doc.setTextColor(255, 255, 255);
  doc.setFontSize(14);
  doc.setFont('helvetica', 'bold');
  doc.text('TERMS AND CONDITIONS - LIABILITY WAIVER', pageWidth / 2, 15, { align: 'center' });
  
  let waiverY = 35;
  
  // Legal sections
  const legalSections = [
    {
      title: '1. RESPONSIBILITY FOR DAMAGES',
      content: 'The Renter acknowledges and agrees that they are fully responsible for any and all damages to the rental vehicle during the rental period, regardless of fault, including but not limited to: collision damage, vandalism, theft, weather-related damage, interior damage, mechanical damage resulting from misuse, and damage to tires, wheels, windshield, and undercarriage. The Renter agrees to pay for all repair costs or the fair market value of the vehicle if it is deemed a total loss.'
    },
    {
      title: '2. TRAFFIC VIOLATIONS AND TICKETS',
      content: 'The Renter is solely responsible for any and all traffic violations, parking tickets, toll violations, photo enforcement tickets, and any other citations or fines incurred during the rental period. The Renter authorizes Ak Car Rental to charge their credit card on file for any such violations plus an administrative fee of $50.00 per violation.'
    },
    {
      title: '3. NO SMOKING POLICY',
      content: 'Smoking, vaping, or the use of any tobacco or cannabis products inside the rental vehicle is strictly prohibited. If evidence of smoking is detected upon vehicle return, a cleaning and detailing fee of $500.00 will be charged to the Renter\'s credit card on file.'
    },
    {
      title: '4. FUEL POLICY',
      content: 'The vehicle must be returned with the same fuel level as at pickup. If returned with less fuel, the Renter will be charged $35.00 for each quarter tank (1/4) missing. Example: 3/4 tank = $35; 1/2 tank = $70; 1/4 tank = $105; empty = $140.'
    },
    {
      title: '5. VEHICLE CLEANLINESS',
      content: 'The vehicle must be returned in reasonably clean condition. If returned excessively dirty (mud, sand, pet hair, food debris, stains, garbage), a detailing fee of $150.00 will be charged to the Renter\'s credit card on file.'
    },
    {
      title: '6. INSURANCE AND LIABILITY',
      content: 'The Renter acknowledges that the basic rental rate does not include comprehensive insurance coverage. The Renter is responsible for understanding their own insurance coverage and agrees to bear any costs not covered by insurance.'
    },
    {
      title: '7. PROHIBITED USES',
      content: 'The vehicle shall not be used for: illegal purposes, towing, racing, off-road driving, transporting hazardous materials, transporting pets without prior approval, or subletting to any other party. Violation may result in immediate termination and full liability.'
    },
    {
      title: '8. RETURN CONDITIONS',
      content: 'The vehicle must be returned on time with all accessories, keys, and documents. Late returns incur additional daily charges plus a $25.00 late fee. Failure to return within 24 hours without contact may be reported as theft.'
    },
    {
      title: '9. INDEMNIFICATION',
      content: 'The Renter agrees to indemnify and hold harmless Ak Car Rental from all claims, damages, losses, and expenses arising from the Renter\'s use of the vehicle or breach of this agreement.'
    }
  ];
  
  doc.setFontSize(8);
  
  legalSections.forEach((section, index) => {
    // Check if we need a new page
    if (waiverY > 250) {
      doc.addPage();
      waiverY = 20;
    }
    
    // Title
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(...primaryColor);
    doc.text(section.title, 15, waiverY);
    waiverY += 4;
    
    // Content
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(...mutedColor);
    const lines = doc.splitTextToSize(section.content, pageWidth - 30);
    doc.text(lines, 15, waiverY);
    waiverY += lines.length * 3.5 + 6;
  });
  
  // Fees Summary Table
  if (waiverY > 200) {
    doc.addPage();
    waiverY = 20;
  }
  
  waiverY += 5;
  doc.setFillColor(220, 38, 38); // Red background
  doc.rect(10, waiverY - 3, pageWidth - 20, 8, 'F');
  doc.setTextColor(255, 255, 255);
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(9);
  doc.text('FEES SUMMARY', 15, waiverY + 2);
  waiverY += 10;
  
  const fees = [
    { item: 'Smoking/Vaping in Vehicle', fee: '$500.00' },
    { item: 'Missing Fuel (per quarter tank)', fee: '$35.00' },
    { item: 'Excessive Dirt/Cleaning Required', fee: '$150.00' },
    { item: 'Traffic Violation Admin Fee', fee: '$50.00/violation' },
    { item: 'Late Return Fee', fee: '$25.00 + daily rate' }
  ];
  
  doc.setFontSize(8);
  fees.forEach((fee, index) => {
    const isEven = index % 2 === 0;
    if (isEven) {
      doc.setFillColor(254, 226, 226); // Light red
      doc.rect(10, waiverY - 3, pageWidth - 20, 7, 'F');
    }
    doc.setTextColor(...mutedColor);
    doc.setFont('helvetica', 'normal');
    doc.text(fee.item, 15, waiverY + 1);
    doc.setTextColor(220, 38, 38);
    doc.setFont('helvetica', 'bold');
    doc.text(fee.fee, pageWidth - 15, waiverY + 1, { align: 'right' });
    waiverY += 7;
  });
  
  // Acknowledgment section
  waiverY += 10;
  doc.setDrawColor(202, 138, 4);
  doc.setLineWidth(0.5);
  doc.roundedRect(10, waiverY - 3, pageWidth - 20, 35, 3, 3, 'S');
  
  doc.setFontSize(9);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(0, 0, 0);
  doc.text('ACKNOWLEDGMENT', 15, waiverY + 5);
  
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(8);
  doc.setTextColor(...mutedColor);
  const ackText = 'By signing this agreement, I acknowledge that I have read, understood, and agree to be bound by all the terms and conditions stated above. I understand that I am financially responsible for any damages to the vehicle, all traffic violations, and any applicable fees for smoking ($500), fuel ($35/quarter tank), or cleaning ($150) as outlined above.';
  const ackLines = doc.splitTextToSize(ackText, pageWidth - 40);
  doc.text(ackLines, 15, waiverY + 12);
  
  // Waiver acceptance checkbox
  waiverY += 38;
  
  if (signatureInfo?.waiverAccepted) {
    doc.setFillColor(...successColor);
    doc.rect(15, waiverY, 6, 6, 'F');
    doc.setTextColor(255, 255, 255);
    doc.setFontSize(10);
    doc.text('✓', 16, waiverY + 5);
  } else {
    doc.setDrawColor(150, 150, 150);
    doc.rect(15, waiverY, 6, 6, 'S');
  }
  
  doc.setFontSize(9);
  doc.setTextColor(0, 0, 0);
  doc.setFont('helvetica', 'bold');
  doc.text('I have read and agree to all terms and conditions', 25, waiverY + 5);
  
  if (signatureInfo?.waiverAcceptedAt) {
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(...mutedColor);
    doc.setFontSize(8);
    doc.text(`Accepted on: ${formatDate(signatureInfo.waiverAcceptedAt)}`, 25, waiverY + 11);
  }
  
  // Waiver Initials Box
  waiverY += 18;
  doc.setFillColor(240, 243, 247);
  doc.roundedRect(15, waiverY - 3, pageWidth - 30, 22, 3, 3, 'F');
  
  doc.setFontSize(9);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(...primaryColor);
  doc.text('CUSTOMER INITIALS', 20, waiverY + 4);
  
  doc.setFontSize(8);
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(...mutedColor);
  doc.text('I acknowledge I have read and understood all terms, conditions, and fees listed above.', 20, waiverY + 10);
  
  // Initials box
  doc.setDrawColor(...primaryColor);
  doc.setLineWidth(0.5);
  doc.roundedRect(20, waiverY + 13, 30, 10, 2, 2, 'S');
  
  if (signatureInfo?.waiverInitials) {
    doc.setFontSize(12);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(...textColor);
    doc.text(signatureInfo.waiverInitials, 35, waiverY + 20, { align: 'center' });
  } else {
    doc.setFontSize(7);
    doc.setTextColor(...mutedColor);
    doc.text('INITIALS', 35, waiverY + 19, { align: 'center' });
  }
  
  // === SIGNATURE SECTION ===
  const sigY = waiverY + 32;
  
  // Section header
  doc.setFontSize(10);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(...primaryColor);
  doc.text('SIGNATURES & ACKNOWLEDGMENT', 15, sigY);
  
  doc.setFontSize(8);
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(...mutedColor);
  doc.text('By signing below, I acknowledge that I have read and agree to all terms and conditions of this rental agreement.', 15, sigY + 7, { maxWidth: pageWidth - 30 });
  
  // Renter Signature Box
  const renterSigY = sigY + 18;
  doc.setDrawColor(30, 64, 124);
  doc.setLineWidth(0.5);
  doc.roundedRect(15, renterSigY, 80, 30, 2, 2, 'S');
  
  // Check if renter has digitally signed
  if (signatureInfo?.renterSignature) {
    // Add digital signature image
    try {
      doc.addImage(signatureInfo.renterSignature, 'PNG', 17, renterSigY + 2, 76, 26);
      // Add "SIGNED" badge
      doc.setFillColor(...successColor);
      doc.roundedRect(15, renterSigY, 20, 7, 2, 0, 'F');
      doc.setTextColor(255, 255, 255);
      doc.setFontSize(5);
      doc.setFont('helvetica', 'bold');
      doc.text('SIGNED', 17, renterSigY + 5);
    } catch (e) {
      console.error('Error adding renter signature to PDF:', e);
    }
  } else {
    // "SIGN HERE" label with arrow
    doc.setFillColor(30, 64, 124);
    doc.roundedRect(15, renterSigY, 25, 8, 2, 0, 'F');
    doc.setTextColor(255, 255, 255);
    doc.setFontSize(6);
    doc.setFont('helvetica', 'bold');
    doc.text('SIGN HERE', 17, renterSigY + 5.5);
    
    // X mark for signature
    doc.setTextColor(...primaryColor);
    doc.setFontSize(14);
    doc.text('X', 20, renterSigY + 22);
    
    // Signature line inside box
    doc.setDrawColor(200, 200, 200);
    doc.setLineWidth(0.3);
    doc.line(28, renterSigY + 22, 90, renterSigY + 22);
  }
  
  // Labels
  doc.setFontSize(8);
  doc.setTextColor(...mutedColor);
  doc.setFont('helvetica', 'normal');
  doc.text(`Renter: ${renterInfo?.name || ''}`, 15, renterSigY + 36);
  
  // Date for renter
  if (signatureInfo?.renterSignedAt) {
    doc.text(`Signed: ${formatDate(signatureInfo.renterSignedAt)}`, 15, renterSigY + 42);
  } else {
    doc.setDrawColor(200, 200, 200);
    doc.roundedRect(15, renterSigY + 40, 40, 12, 2, 2, 'S');
    doc.text('Date:', 17, renterSigY + 48);
  }
  
  // Agent Signature Box
  const agentSigY = renterSigY;
  doc.setDrawColor(30, 64, 124);
  doc.setLineWidth(0.5);
  doc.roundedRect(110, agentSigY, 80, 30, 2, 2, 'S');
  
  // Check if agent has digitally signed
  if (signatureInfo?.agentSignature) {
    // Add digital signature image
    try {
      doc.addImage(signatureInfo.agentSignature, 'PNG', 112, agentSigY + 2, 76, 26);
      // Add "SIGNED" badge
      doc.setFillColor(...successColor);
      doc.roundedRect(110, agentSigY, 20, 7, 2, 0, 'F');
      doc.setTextColor(255, 255, 255);
      doc.setFontSize(5);
      doc.setFont('helvetica', 'bold');
      doc.text('SIGNED', 112, agentSigY + 5);
    } catch (e) {
      console.error('Error adding agent signature to PDF:', e);
    }
  } else {
    // "SIGN HERE" label
    doc.setFillColor(30, 64, 124);
    doc.roundedRect(110, agentSigY, 25, 8, 2, 0, 'F');
    doc.setTextColor(255, 255, 255);
    doc.setFontSize(6);
    doc.setFont('helvetica', 'bold');
    doc.text('SIGN HERE', 112, agentSigY + 5.5);
    
    // X mark for signature
    doc.setTextColor(...primaryColor);
    doc.setFontSize(14);
    doc.text('X', 115, agentSigY + 22);
    
    // Signature line inside box
    doc.setDrawColor(200, 200, 200);
    doc.setLineWidth(0.3);
    doc.line(123, agentSigY + 22, 185, agentSigY + 22);
  }
  
  // Labels
  doc.setFontSize(8);
  doc.setTextColor(...mutedColor);
  doc.setFont('helvetica', 'normal');
  doc.text(`Agent: ${signatureInfo?.agentName || 'Ak Car Rental'}`, 110, agentSigY + 36);
  
  // Date for agent
  if (signatureInfo?.agentSignedAt) {
    doc.text(`Signed: ${formatDate(signatureInfo.agentSignedAt)}`, 110, agentSigY + 42);
  } else {
    doc.setDrawColor(200, 200, 200);
    doc.roundedRect(110, agentSigY + 40, 40, 12, 2, 2, 'S');
    doc.text('Date:', 112, agentSigY + 48);
  }
  
  // === FOOTER ===
  const footerY = 285;
  doc.setDrawColor(220, 220, 220);
  doc.line(15, footerY - 5, pageWidth - 15, footerY - 5);
  
  doc.setFontSize(8);
  doc.setTextColor(...mutedColor);
  doc.text(
    `${companyInfo?.name || 'Ak Car Rental'} • ${companyInfo?.phone || ''} • ${companyInfo?.email || ''}`,
    pageWidth / 2,
    footerY,
    { align: 'center' }
  );
  
  doc.setFontSize(7);
  doc.text(
    'By signing, the renter agrees to the terms and conditions of this rental agreement.',
    pageWidth / 2,
    footerY + 6,
    { align: 'center' }
  );
  
  return doc;
};

/**
 * Download agreement as PDF
 * @param {Object} agreement - The agreement data
 */
export const downloadAgreementPDF = (agreement) => {
  const doc = generateAgreementPDF(agreement);
  doc.save(`${agreement.agreementInfo?.number || 'agreement'}.pdf`);
};

/**
 * Print agreement as PDF (opens in new window for printing)
 * @param {Object} agreement - The agreement data
 */
export const printAgreementPDF = (agreement) => {
  const doc = generateAgreementPDF(agreement);
  // Open PDF in new window for printing
  const pdfBlob = doc.output('blob');
  const pdfUrl = URL.createObjectURL(pdfBlob);
  const printWindow = window.open(pdfUrl, '_blank');
  if (printWindow) {
    printWindow.onload = () => {
      printWindow.print();
    };
  }
};

/**
 * Print invoice as PDF (opens in new window for printing)
 * @param {Object} invoice - The invoice data
 */
export const printInvoicePDF = (invoice) => {
  const doc = generateInvoicePDF(invoice);
  const pdfBlob = doc.output('blob');
  const pdfUrl = URL.createObjectURL(pdfBlob);
  const printWindow = window.open(pdfUrl, '_blank');
  if (printWindow) {
    printWindow.onload = () => {
      printWindow.print();
    };
  }
};

/**
 * Get PDF as base64 for email attachment
 * @param {Object} agreement - The agreement data
 * @returns {string} - Base64 encoded PDF
 */
export const getAgreementPDFBase64 = (agreement) => {
  const doc = generateAgreementPDF(agreement);
  return doc.output('datauristring');
};

/**
 * Get Invoice PDF as base64 for email attachment
 * @param {Object} invoice - The invoice data
 * @returns {string} - Base64 encoded PDF
 */
export const getInvoicePDFBase64 = (invoice) => {
  const doc = generateInvoicePDF(invoice);
  return doc.output('datauristring');
};

export default { 
  generateInvoicePDF, 
  downloadInvoicePDF, 
  printInvoicePDF,
  generateAgreementPDF, 
  downloadAgreementPDF,
  printAgreementPDF,
  getAgreementPDFBase64,
  getInvoicePDFBase64
};
